// src/components/ProductCard.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { FiShoppingBag, FiSend } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import { displayName, secondaryName } from "../services/db";
import SampleRequestModal from "./SampleRequestModal";

const eur = (n) => `${Number(n || 0).toFixed(2)} €`;

export default function ProductCard({ product, className }) {
  const { add } = useCart();
  const [askSample, setAskSample] = useState(false);
  const isSample = !!product.isSample;
  const out = !isSample && (Number(product.stock) || 0) < 1;
  const low = !isSample && !out && product.stock <= (product.lowStockAt || 3);

  const name = displayName(product);
  const secondary = secondaryName(product);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    add(product, 1);
  };

  const handleRequest = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAskSample(true);
  };

  return (
    <Card to={`/produktet/${product.id}`} className={className} $out={out}>
      <Visual $hex={product.hex} $out={out} $hasImage={!!product.images?.[0]}>
        {product.images?.[0] && (
          <img src={product.images[0]} alt={name} loading="lazy" />
        )}

        {isSample ? (
          <Pill $sample>Mostër</Pill>
        ) : (
          product.onePigment && <Pill>1 Pigment</Pill>
        )}

        {isSample ? (
          <Status $tone="sample">Vetëm me kërkesë</Status>
        ) : out ? (
          <Status $tone="out">Pa stok</Status>
        ) : low ? (
          <Status $tone="low">Vetëm {product.stock} copë</Status>
        ) : null}

        {isSample ? (
          <AddButton
            onClick={handleRequest}
            aria-label={`Kërko mostrën ${name}`}
          >
            <FiSend />
            <span>Kërko mostrën</span>
          </AddButton>
        ) : (
          !out && (
            <AddButton
              onClick={handleAdd}
              aria-label={`Shto ${name} në shportë`}
            >
              <FiShoppingBag />
              <span>Shto në shportë</span>
            </AddButton>
          )
        )}
      </Visual>

      <Body>
        {(product.pmuCode || product.mbCode) && (
          <Codes>
            {product.pmuCode && <span>{product.pmuCode}</span>}
            {product.mbCode && <MbTag>{product.mbCode}</MbTag>}
          </Codes>
        )}

        <Name>{name}</Name>
        {secondary && <NameDe>{secondary}</NameDe>}

        <Price>
          {isSample ? <Free>Falas për profesionistë</Free> : eur(product.price)}
        </Price>
      </Body>

      {askSample && (
        <SampleRequestModal
          product={product}
          onClose={() => setAskSample(false)}
        />
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.color.bg};
  transition:
    transform 0.35s ${({ theme }) => theme.ease.out},
    box-shadow 0.35s ${({ theme }) => theme.ease.out};

  /* Borderless with a soft lift on hover — a 1px outline on every tile
     is what made the grid read as dated. */
  &:hover {
    transform: translateY(-6px);
    box-shadow: ${({ theme }) => theme.shadow.lg};
  }

  ${({ $out }) =>
    $out &&
    css`
      opacity: 0.72;
    `}
`;

const Visual = styled.div`
  position: relative;
  /* Square by default — most product photos are square, and a square tile
     letterboxes every other ratio gracefully instead of cropping it. */
  aspect-ratio: 1 / 1;
  border-radius: ${({ theme }) => theme.radius.lg};
  /* A photo sits on neutral; a pure shade swatch fills with its own colour. */
  background: ${({ $hex, $hasImage, theme }) =>
    $hasImage ? theme.color.bgSoft : $hex || theme.color.bgSoft};
  overflow: hidden;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    /* contain, never cover — nothing gets cut off at any aspect ratio */
    object-fit: contain;
    object-position: center;
    padding: 12%;
    transition: transform 0.6s ${({ theme }) => theme.ease.out};
  }

  ${Card}:hover & img {
    transform: scale(1.05);
  }

  ${({ $out }) =>
    $out &&
    css`
      filter: grayscale(0.7);
    `}
`;

const Pill = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 5px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ $sample, theme }) =>
    $sample ? theme.color.ink : "rgba(255, 255, 255, 0.92)"};
  color: ${({ $sample, theme }) => ($sample ? "#fff" : theme.color.ink)};
  font-size: 0.62rem;
  font-weight: ${({ theme }) => theme.weight.bold};
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const Status = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 5px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 0.62rem;
  font-weight: ${({ theme }) => theme.weight.bold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #fff;
  background: ${({ $tone, theme }) =>
    $tone === "out"
      ? theme.color.ink
      : $tone === "sample"
        ? theme.color.brand
        : theme.color.warn};
`;

const AddButton = styled.button`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(6px);
  color: ${({ theme }) => theme.color.ink};
  font-size: ${({ theme }) => theme.size.xs};
  font-weight: ${({ theme }) => theme.weight.semi};

  opacity: 0;
  transform: translateY(10px);
  transition:
    opacity 0.3s ${({ theme }) => theme.ease.out},
    transform 0.3s ${({ theme }) => theme.ease.out},
    background 0.25s;

  ${Card}:hover &,
  &:focus-visible {
    opacity: 1;
    transform: translateY(0);
  }

  &:hover {
    background: ${({ theme }) => theme.color.brand};
    color: #fff;
  }

  /* touch devices never hover — keep it permanently visible there */
  @media (hover: none) {
    opacity: 1;
    transform: none;
  }
`;

const Body = styled.div`
  padding: 18px 4px 6px;
`;

const Codes = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.66rem;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.color.muted};
`;

const MbTag = styled.span`
  padding: 2px 6px;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.brandSoft};
  color: ${({ theme }) => theme.color.brandDark};
  font-weight: ${({ theme }) => theme.weight.semi};
`;

const Name = styled.h3`
  margin: 9px 0 2px;
  font-size: 1.08rem;
  font-weight: ${({ theme }) => theme.weight.medium};
`;

const NameDe = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.muted};
  font-style: italic;
`;

const Free = styled.span`
  font-family: ${({ theme }) => theme.font.sans};
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  color: ${({ theme }) => theme.color.brand};
  letter-spacing: 0;
`;

const Price = styled.p`
  margin: 12px 0 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: 1.12rem;
  font-weight: ${({ theme }) => theme.weight.medium};
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.color.ink};
`;
